import { useState, useEffect } from 'react';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Truck, Eye, X as XIcon, UserPlus, Pencil, Trash, TelegramLogo, LinkBreak, PaperPlaneTilt, Copy, CaretDown, CaretRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useDataCache } from '@/contexts/DataCache';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Shipments = () => {
  const { deliverers, sellers, shipments, products, refreshDeliverers, refreshShipments, ready } = useDataCache();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [loading, setLoading] = useState(!ready.shipments);
  const [showDelivererDialog, setShowDelivererDialog] = useState(false);
  const [editingDeliverer, setEditingDeliverer] = useState(null);
  const [delivererForm, setDelivererForm] = useState({ name: '', phone: '', nip: '', default_keeps_shipping: false });
  const [filterStatus, setFilterStatus] = useState('all');
  const [deliverersExpanded, setDeliverersExpanded] = useState(() => {
    try { return localStorage.getItem('shipments_deliverers_expanded_v1') === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('shipments_deliverers_expanded_v1', deliverersExpanded ? '1' : '0'); } catch { /* */ }
  }, [deliverersExpanded]);
  const [assignDialog, setAssignDialog] = useState({ open: false, shipment: null });
  const [assignForm, setAssignForm] = useState({ deliverer_id: '', deliverer_keeps_shipping: false });
  const [detailDialog, setDetailDialog] = useState({ open: false, shipment: null });
  const [botUsername, setBotUsername] = useState(null);
  const [tgDialog, setTgDialog] = useState({ open: false, deliverer: null, code: null, loading: false });

  // Edición de items de envío no pagado
  const [editItemsDlg, setEditItemsDlg] = useState({ open: false, shipment: null, items: [], reason: '', search: '', saving: false });

  // Cambiar vendedor de un envío ya foliado
  const [changeSellerDlg, setChangeSellerDlg] = useState({ open: false, shipment: null, seller_id: '', reason: '', saving: false });

  const openChangeSeller = (sh) => {
    setChangeSellerDlg({ open: true, shipment: sh, seller_id: sh.seller_id || '', reason: '', saving: false });
  };

  const updateShipmentVerification = async (shipmentId, patch) => {
    try {
      await axios.patch(
        `${API}/shipments/${shipmentId}/verification`,
        patch,
        { withCredentials: true }
      );
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar verificación');
    }
  };

  const changeBranch = async (sh) => {
    let BR = {};
    try {
      const { data: branches } = await axios.get(`${API}/branches/list`, { withCredentials: true });
      BR = Object.fromEntries((branches || []).map(b => [b.id, b.name]));
    } catch { toast.error('No se pudieron cargar las sucursales'); return; }
    const target = Object.keys(BR).find(id => id !== sh.branch_id);
    if (!target) return;
    const reason = window.prompt(
      `¿Mover el envío ${sh.folio} a ${BR[target]}?\n\nEl stock se descontará de ${BR[target]} y la venta, comisión y deuda del entregador quedarán en ${BR[target]}.\n\nMotivo (opcional):`, ''
    );
    if (reason === null) return;
    try {
      await axios.post(`${API}/shipments/${sh.id}/change-branch`, { branch_id: target, reason }, { withCredentials: true });
      toast.success(`Envío ${sh.folio} movido a ${BR[target]}`);
      refreshShipments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al mover el envío');
    }
  };

  const submitChangeSeller = async () => {
    const { shipment, seller_id, reason } = changeSellerDlg;
    if (!seller_id) { toast.error('Selecciona un vendedor'); return; }
    if (seller_id === shipment.seller_id) { toast.error('Ese ya es el vendedor actual'); return; }
    const r = (reason || '').trim();
    if (!isAdmin && !r) { toast.error('Indica el motivo del cambio'); return; }
    setChangeSellerDlg(prev => ({ ...prev, saving: true }));
    try {
      const { data } = await axios.put(
        `${API}/shipments/${shipment.id}/change-seller`,
        { seller_id, reason: r },
        { withCredentials: true }
      );
      if (data?.status === 'pending') {
        toast.success('✅ Solicitud enviada al aprobador por Telegram');
      } else {
        toast.success('Vendedor actualizado');
      }
      setChangeSellerDlg({ open: false, shipment: null, seller_id: '', reason: '', saving: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar vendedor');
      setChangeSellerDlg(prev => ({ ...prev, saving: false }));
    }
  };

  useEffect(() => {
    // Si el cache aún no está listo, refrescar (in-flight). Si ya está, sólo refrescar en background.
    refreshShipments().finally(() => setLoading(false));
    axios.get(`${API}/telegram/bot-info`, { withCredentials: true })
      .then((r) => setBotUsername(r.data.username))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresca entregadores (cache global) + envíos (cache global) tras mutaciones
  const fetchData = async () => {
    await Promise.all([refreshDeliverers(), refreshShipments()]);
  };

  const openAssignDialog = (shipment) => {
    // Si ya estaba asignado, conservar valores; si no, esperamos a la selección del entregador
    setAssignForm({
      deliverer_id: shipment.deliverer_id || '',
      deliverer_keeps_shipping: shipment.deliverer_keeps_shipping || false
    });
    setAssignDialog({ open: true, shipment });
  };

  const handleAssignDelivererChange = (delivererId) => {
    const d = deliverers.find((x) => x.id === delivererId);
    setAssignForm({
      deliverer_id: delivererId,
      // Si el entregador tiene default_keeps_shipping, lo prellenamos
      deliverer_keeps_shipping: d?.default_keeps_shipping || false
    });
  };

  const submitAssign = async () => {
    if (!assignForm.deliverer_id) {
      toast.error('Selecciona un entregador');
      return;
    }
    try {
      const { data } = await axios.put(
        `${API}/shipments/${assignDialog.shipment.id}/assign`,
        assignForm,
        { withCredentials: true }
      );
      if (data?.status === 'pending') {
        toast.success('✅ Solicitud enviada al aprobador por Telegram');
      } else {
        toast.success('Entregador asignado');
      }
      setAssignDialog({ open: false, shipment: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar entregador');
    }
  };

  const cancelShipment = async (sh) => {
    const reason = window.prompt(
      `Motivo de cancelación del envío ${sh.folio}:\n\n(Se enviará al aprobador por Telegram)`,
      ''
    );
    if (reason === null) return; // canceló el prompt
    const r = (reason || '').trim();
    if (!r) { toast.error('Debes indicar el motivo'); return; }
    try {
      const { data } = await axios.delete(
        `${API}/shipments/${sh.id}?reason=${encodeURIComponent(r)}`,
        { withCredentials: true }
      );
      if (data?.status === 'pending') {
        toast.success('✅ Solicitud enviada al aprobador por Telegram');
      } else {
        toast.success('Envío cancelado');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar envío');
    }
  };

  // ----- Editar items del envío -----
  const openEditItems = (sh) => {
    const items = (sh.items || []).map(it => ({
      product_id: it.product_id,
      product_name: it.product_name,
      variant_id: it.variant_id || null,
      variant_name: it.variant_name || null,
      quantity: Number(it.quantity) || 1,
      unit_price: Number(it.unit_price) || 0,
      price_type: it.price_type || 'precio_1',
    }));
    setEditItemsDlg({ open: true, shipment: sh, items, reason: '', search: '', saving: false });
    setDetailDialog({ open: false, shipment: null });
  };

  const updateEditItem = (idx, field, value) => {
    setEditItemsDlg(s => {
      const items = [...s.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...s, items };
    });
  };

  const removeEditItem = (idx) => {
    setEditItemsDlg(s => ({ ...s, items: s.items.filter((_, i) => i !== idx) }));
  };

  const addProductToEdit = (p) => {
    setEditItemsDlg(s => {
      // Si ya existe el producto, incrementar cantidad
      const existing = s.items.findIndex(it => it.product_id === p.id);
      if (existing >= 0) {
        const items = [...s.items];
        items[existing] = { ...items[existing], quantity: Number(items[existing].quantity) + 1 };
        return { ...s, items, search: '' };
      }
      return {
        ...s,
        search: '',
        items: [
          ...s.items,
          {
            product_id: p.id,
            product_name: p.name,
            variant_id: null,
            variant_name: null,
            quantity: 1,
            unit_price: Number(p.precio_1) || 0,
            price_type: 'precio_1',
          },
        ],
      };
    });
  };

  const submitEditItems = async () => {
    const items = editItemsDlg.items
      .filter(it => it.product_id && Number(it.quantity) > 0)
      .map(it => ({
        product_id: it.product_id,
        variant_id: it.variant_id || undefined,
        quantity: Number(it.quantity),
        unit_price: Number(it.unit_price) || 0,
        price_type: it.price_type || 'precio_1',
      }));
    if (items.length === 0) { toast.error('Debe quedar al menos un producto'); return; }
    if (!isAdmin && !editItemsDlg.reason.trim()) {
      toast.error('Indica el motivo para solicitar aprobación');
      return;
    }
    setEditItemsDlg(s => ({ ...s, saving: true }));
    try {
      const { data } = await axios.post(
        `${API}/shipments/${editItemsDlg.shipment.id}/edit-items`,
        { items, reason: editItemsDlg.reason.trim() },
        { withCredentials: true }
      );
      if (data?.status === 'pending') {
        toast.success('✅ Solicitud enviada al admin para aprobación');
      } else {
        toast.success(`Items actualizados · nuevo total $${(data.new_items_total || 0).toFixed(2)}`);
      }
      setEditItemsDlg({ open: false, shipment: null, items: [], reason: '', search: '', saving: false });
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al editar items');
      setEditItemsDlg(s => ({ ...s, saving: false }));
    }
  };

  const openDelivererDialog = (d = null) => {
    if (d) {
      setEditingDeliverer(d);
      setDelivererForm({
        name: d.name,
        phone: d.phone || '',
        nip: d.nip || '',
        default_keeps_shipping: d.default_keeps_shipping || false
      });
    } else {
      setEditingDeliverer(null);
      setDelivererForm({ name: '', phone: '', nip: '', default_keeps_shipping: false });
    }
    setShowDelivererDialog(true);
  };

  const submitDeliverer = async (e) => {
    e.preventDefault();
    // Validar NIP si se proporcionó
    const nipTrim = (delivererForm.nip || '').trim();
    if (nipTrim && !/^\d{4}$/.test(nipTrim)) {
      toast.error('El NIP debe ser exactamente 4 dígitos');
      return;
    }
    const payload = { ...delivererForm };
    // Si el NIP viene vacío al crear, no enviar el campo para que el backend autogenere.
    // En edición, un NIP vacío significa "no cambiar" — tampoco enviarlo.
    if (!nipTrim) delete payload.nip;
    else payload.nip = nipTrim;
    try {
      if (editingDeliverer) {
        await axios.put(`${API}/deliverers/${editingDeliverer.id}`, payload, { withCredentials: true });
        toast.success('Entregador actualizado');
      } else {
        await axios.post(`${API}/deliverers`, payload, { withCredentials: true });
        toast.success('Entregador creado');
      }
      setShowDelivererDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar entregador');
    }
  };

  const deleteDeliverer = async (d) => {
    if (!window.confirm(`¿Eliminar al entregador "${d.name}"?`)) return;
    try {
      await axios.delete(`${API}/deliverers/${d.id}`, { withCredentials: true });
      toast.success('Entregador eliminado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar entregador');
    }
  };

  // ----- Telegram dialog -----
  const openTelegramDialog = (deliverer) => {
    setTgDialog({ open: true, deliverer, code: null, loading: false });
  };

  const generateTgCode = async () => {
    setTgDialog((d) => ({ ...d, loading: true }));
    try {
      const { data } = await axios.post(`${API}/deliverers/${tgDialog.deliverer.id}/telegram/code`, {}, { withCredentials: true });
      setTgDialog((d) => ({ ...d, code: data.code, loading: false }));
      toast.success('Código generado');
    } catch (error) {
      toast.error('Error al generar código');
      setTgDialog((d) => ({ ...d, loading: false }));
    }
  };

  const sendTgTestMessage = async () => {
    try {
      await axios.post(`${API}/deliverers/${tgDialog.deliverer.id}/telegram/test`, {}, { withCredentials: true });
      toast.success('Mensaje de prueba enviado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo enviar el mensaje');
    }
  };

  const unlinkTg = async () => {
    if (!window.confirm('¿Desvincular Telegram de este entregador? Dejará de recibir notificaciones.')) return;
    try {
      await axios.post(`${API}/deliverers/${tgDialog.deliverer.id}/telegram/unlink`, {}, { withCredentials: true });
      toast.success('Telegram desvinculado');
      setTgDialog({ open: false, deliverer: null, code: null, loading: false });
      fetchData();
    } catch (error) {
      toast.error('Error al desvincular');
    }
  };

  const copyToClipboard = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success('Copiado');
  };

  const tgStartLink = botUsername && tgDialog.code
    ? `https://t.me/${botUsername}?start=${tgDialog.code}`
    : null;

  const currentDelivererInDialog = tgDialog.deliverer
    ? deliverers.find((d) => d.id === tgDialog.deliverer.id) || tgDialog.deliverer
    : null;

  const createDeliverer = submitDeliverer; // alias para retrocompatibilidad de markup

  const filteredShipments = filterStatus === 'all'
    ? shipments
    : shipments.filter(s => s.status === filterStatus);

  const sellerName = (id) => sellers.find(s => s.id === id)?.name || 'N/A';
  const delivererName = (id) => deliverers.find(d => d.id === id)?.name || '—';

  const statusBadge = (status) => {
    const map = {
      por_asignar: { label: 'Por asignar', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
      assigned: { label: 'Asignado', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
      paid: { label: 'Pagado', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' }
    };
    const s = map[status] || { label: status, cls: 'bg-slate-100' };
    return <span className={`px-2 py-0.5 rounded-full text-xs border whitespace-nowrap inline-block ${s.cls}`}>{s.label}</span>;
  };

  const calcDebt = (sh) => {
    let d = sh.items_total || 0;
    if (!sh.deliverer_keeps_shipping) d += sh.shipping_cost || 0;
    return d;
  };

  return (
    <MainLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Envíos y Entregadores</h1>
            <p className="text-muted-foreground mt-1">Asigna entregadores y gestiona envíos pendientes</p>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const { data } = await axios.post(`${API}/admin/notify/deliverer-debts-now`, {}, { withCredentials: true });
                    toast.success(`🌙 Notificaciones enviadas: ${data.sent} entregador(es)`);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Error al enviar notificaciones');
                  }
                }}
                data-testid="notify-deliverer-debts-now"
                title="Envía ahora el resumen de deuda a cada entregador con Telegram vinculado y balance > 0"
              >
                <PaperPlaneTilt size={18} className="mr-2" />
                Cerrar día
              </Button>
            )}
            <Button onClick={() => openDelivererDialog()} data-testid="add-deliverer-button">
              <UserPlus size={20} weight="bold" className="mr-2" />
              Nuevo Entregador
            </Button>
          </div>
        </div>

        {/* Deliverers cards (colapsable para no ocupar espacio) */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setDeliverersExpanded(v => !v)}
            className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-violet-700 transition-colors"
            data-testid="deliverers-toggle"
          >
            {deliverersExpanded ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
            <span>Entregadores ({deliverers.length})</span>
            {!deliverersExpanded && deliverers.some(d => (d.balance_owed || 0) > 0) && (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                {deliverers.filter(d => (d.balance_owed || 0) > 0).length} con deuda
              </span>
            )}
          </button>
        </div>
        {deliverersExpanded && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {deliverers.map((d) => (
            <div key={d.id} className="bg-white border border-border rounded-sm p-4 relative" data-testid={`deliverer-card-${d.id}`}>
              <div className="absolute top-2 right-2 flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => openTelegramDialog(d)} data-testid={`telegram-deliverer-${d.id}`} title="Configurar Telegram">
                  <TelegramLogo size={14} weight={d.telegram_chat_id ? 'fill' : 'regular'} className={d.telegram_chat_id ? 'text-blue-500' : ''} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => openDelivererDialog(d)} data-testid={`edit-deliverer-${d.id}`}>
                  <Pencil size={14} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => deleteDeliverer(d)} data-testid={`delete-deliverer-${d.id}`}>
                  <Trash size={14} />
                </Button>
              </div>
              <h3 className="font-semibold text-lg pr-24">{d.name}</h3>
              <p className="text-sm text-muted-foreground">{d.phone || 'Sin teléfono'}</p>
              {/* NIP para portal de entregador */}
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span className="text-slate-500">NIP portal:</span>
                <span className="font-mono font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded" data-testid={`deliverer-nip-${d.id}`}>
                  {d.nip || '—'}
                </span>
                <button
                  onClick={async () => {
                    if (!window.confirm(`¿Generar un NIP nuevo para ${d.name}? El anterior dejará de funcionar.`)) return;
                    try {
                      const r = await axios.post(`${API}/deliverers/${d.id}/regenerate-nip`, {}, { withCredentials: true });
                      toast.success(`Nuevo NIP: ${r.data.nip}`);
                      fetchData();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Error');
                    }
                  }}
                  className="text-slate-500 hover:text-red-600 underline"
                  data-testid={`regenerate-nip-${d.id}`}
                  title="Generar un NIP nuevo"
                >regenerar</button>
              </div>
              {d.telegram_chat_id ? (
                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700 border border-emerald-300">
                  <TelegramLogo size={10} weight="fill" /> Vinculado
                </span>
              ) : d.telegram_link_code ? (
                <span className="inline-flex mt-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700 border border-amber-300">
                  Vinculación pendiente
                </span>
              ) : null}
              {d.default_keeps_shipping && (
                <p className="text-xs text-emerald-600 mt-1">✓ Siempre se queda con el costo del envío</p>
              )}
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground">Saldo pendiente:</p>
                <p className="text-2xl font-bold text-primary">${(d.balance_owed || 0).toFixed(2)}</p>
              </div>
            </div>
          ))}
          {deliverers.length === 0 && (
            <p className="col-span-full text-muted-foreground text-sm">Sin entregadores. Crea uno con el botón superior.</p>
          )}
        </div>
        )}

        {/* Filter */}
        <div className="flex items-center gap-2 mb-4">
          <Label className="text-sm">Filtro:</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48" data-testid="status-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="por_asignar">Por asignar</SelectItem>
              <SelectItem value="assigned">Asignados</SelectItem>
              <SelectItem value="paid">Pagados</SelectItem>
              <SelectItem value="cancelled">Cancelados</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white border border-border rounded-sm">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="shipments-table">
              <thead className="bg-slate-50 border-b border-border">
                <tr>
                  <th className="px-2 py-3 text-center text-xs font-semibold uppercase text-slate-600" title="Verificación WhatsApp">WA</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600">Folio</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600">Cliente</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600">Vendedor</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600">Entregador</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600">Estatus entregador</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-slate-600">Productos</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-slate-600">Envío</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-slate-600">Deuda</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600">Estado</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredShipments.map((sh) => {
                  const isCancelled = sh.status === 'cancelled';
                  const isPaid = sh.status === 'paid';
                  const rowClass = isCancelled
                    ? 'bg-rose-50 hover:bg-rose-100 cursor-pointer opacity-90'
                    : 'hover:bg-slate-50 cursor-pointer';
                  return (
                  <tr
                    key={sh.id}
                    className={`${rowClass} btn-transition`}
                    data-testid={`shipment-row-${sh.id}`}
                    onDoubleClick={() => setDetailDialog({ open: true, shipment: sh })}
                  >
                    <td className="px-2 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1">
                        <input
                          type="checkbox"
                          checked={!!sh.wa_verified}
                          disabled={!isAdmin}
                          onChange={(e) => isAdmin && updateShipmentVerification(sh.id, { verified: e.target.checked })}
                          className={`h-4 w-4 accent-emerald-600 ${isAdmin ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}
                          title={
                            sh.wa_verified
                              ? `Verificado desde WhatsApp${sh.wa_verified_by ? ` por ${sh.wa_verified_by}` : ''}`
                              : (isAdmin ? 'Marcar como verificado desde WhatsApp' : 'Sin verificar (solo admin puede modificar)')
                          }
                          data-testid={`wa-verify-check-${sh.id}`}
                        />
                        {isAdmin ? (
                          <button
                            type="button"
                            onClick={() => updateShipmentVerification(sh.id, { has_error: !sh.wa_has_error })}
                            className={`px-1.5 py-0.5 rounded text-[11px] font-semibold transition ${sh.wa_has_error ? 'bg-rose-600 text-white hover:bg-rose-700' : 'bg-slate-100 text-slate-500 hover:bg-rose-100 hover:text-rose-700'}`}
                            title={sh.wa_has_error ? 'Hay un error — click para quitar' : 'Marcar como con error'}
                            data-testid={`wa-error-flag-${sh.id}`}
                          >
                            🚩
                          </button>
                        ) : (
                          sh.wa_has_error && (
                            <span
                              className="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-rose-600 text-white cursor-not-allowed"
                              title="Este envío está marcado con error (solo admin puede modificar)"
                              data-testid={`wa-error-flag-readonly-${sh.id}`}
                            >
                              🚩
                            </span>
                          )
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono">
                      <div className={isCancelled ? 'text-rose-700 line-through' : ''}>{sh.folio}</div>
                      {isCancelled && (
                        <div className="text-[11px] mt-1 text-rose-700 not-italic font-semibold flex flex-col gap-0.5" data-testid={`cancelled-reason-${sh.id}`}>
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-600 text-white w-fit">❌ CANCELADO</span>
                          <span className="italic font-normal">{sh.cancelled_reason || 'Sin motivo'}</span>
                          {sh.cancelled_by_name && (
                            <span className="text-[10px] text-rose-500 not-italic">por {sh.cancelled_by_name}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium">{sh.client_name}</div>
                      <div className="text-xs text-muted-foreground">{sh.client_phone}</div>
                    </td>
                    <td className="px-4 py-3 text-sm">{sellerName(sh.seller_id)}</td>
                    <td className="px-4 py-3 text-sm">
                      {sh.deliverer_id ? delivererName(sh.deliverer_id) : <span className="text-amber-600">—</span>}
                      {sh.deliverer_id && sh.deliverer_keeps_shipping && (
                        <div className="text-[10px] text-emerald-600">se queda con costo envío</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {isCancelled ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-bold bg-rose-600 text-white w-fit">
                          ❌ Cancelado
                        </span>
                      ) : !sh.deliverer_id ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : isPaid ? (
                        <div className="flex flex-col gap-0.5" data-testid={`deliverer-status-${sh.id}`}>
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-bold bg-emerald-600 text-white w-fit">
                            💵 PAGADO
                          </span>
                          {sh.paid_at && (
                            <span className="text-[10px] text-muted-foreground">
                              {new Date(sh.paid_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                            </span>
                          )}
                        </div>
                      ) : sh.deliverer_status === 'completed' ? (
                        <div className="flex flex-col gap-0.5" data-testid={`deliverer-status-${sh.id}`}>
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-bold bg-emerald-500 text-white w-fit">
                            ✅ ENTREGADO
                          </span>
                          {sh.deliverer_completed_at && (
                            <span className="text-[10px] text-muted-foreground">
                              {new Date(sh.deliverer_completed_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium bg-amber-100 text-amber-800 whitespace-nowrap" data-testid={`deliverer-status-${sh.id}`}>
                          ⏳ En curso
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">${(sh.items_total || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right">${(sh.shipping_cost || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold">
                      {sh.status === 'paid' ? '—' : `$${calcDebt(sh).toFixed(2)}`}
                    </td>
                    <td className="px-4 py-3 text-sm">{statusBadge(sh.status)}</td>
                    <td className="px-4 py-3 text-sm text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); setDetailDialog({ open: true, shipment: sh }); }}
                          data-testid={`view-shipment-${sh.id}`}
                          title="Ver detalle"
                        >
                          <Eye size={16} />
                        </Button>
                        {sh.status !== 'paid' && sh.status !== 'cancelled' && (
                          <Button
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); openAssignDialog(sh); }}
                            data-testid={`assign-shipment-${sh.id}`}
                          >
                            <Truck size={16} className="mr-1" />
                            {sh.status === 'assigned' ? 'Reasignar' : 'Asignar'}
                          </Button>
                        )}
                        {sh.status !== 'paid' && sh.status !== 'cancelled' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); openEditItems(sh); }}
                            data-testid={`edit-items-shipment-${sh.id}`}
                            title={isAdmin ? 'Editar productos del envío' : 'Solicitar aprobación para editar productos'}
                            className="border-amber-400 text-amber-700 hover:bg-amber-50"
                          >
                            <Pencil size={16} className="mr-1" />
                            Items
                          </Button>
                        )}
                        {sh.status !== 'paid' && sh.status !== 'cancelled' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); openChangeSeller(sh); }}
                            data-testid={`change-seller-shipment-${sh.id}`}
                            title={isAdmin ? 'Cambiar vendedor' : 'Solicitar cambio de vendedor (requiere aprobación)'}
                            className="border-indigo-400 text-indigo-700 hover:bg-indigo-50"
                          >
                            <UserPlus size={16} className="mr-1" />
                            Vendedor
                          </Button>
                        )}
                        {isAdmin && sh.status !== 'paid' && sh.status !== 'cancelled' && !sh.delivered_at && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); changeBranch(sh); }}
                            data-testid={`change-branch-shipment-${sh.id}`}
                            title="Sale de otra sucursal: mover stock, venta, comisión y deuda"
                            className="border-teal-400 text-teal-700 hover:bg-teal-50"
                          >
                            🔀 Sucursal
                          </Button>
                        )}
                        {sh.status !== 'paid' && sh.status !== 'cancelled' && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={(e) => { e.stopPropagation(); cancelShipment(sh); }}
                            data-testid={`cancel-shipment-${sh.id}`}
                            title="Solicitar cancelación (requiere aprobación)"
                          >
                            <XIcon size={16} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {filteredShipments.length === 0 && !loading && (
                  <tr>
                    <td colSpan="10" className="px-4 py-8 text-center text-muted-foreground">
                      No hay envíos que coincidan
                    </td>
                  </tr>
                )}
                {loading && (
                  <tr>
                    <td colSpan="10" className="px-4 py-8 text-center text-muted-foreground">
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded-full bg-slate-300 animate-pulse"></span>
                        Cargando envíos…
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-muted-foreground mt-2">Doble clic en un envío para ver el detalle completo.</p>
      </div>

      {/* Assign Deliverer Dialog */}
      <Dialog open={assignDialog.open} onOpenChange={(o) => !o && setAssignDialog({ open: false, shipment: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Asignar Entregador — {assignDialog.shipment?.folio}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 p-3 rounded text-sm space-y-1">
              <div className="flex justify-between">
                <span>Productos:</span>
                <span className="font-medium">${(assignDialog.shipment?.items_total || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Costo de envío:</span>
                <span className="font-medium">${(assignDialog.shipment?.shipping_cost || 0).toFixed(2)}</span>
              </div>
            </div>
            <div>
              <Label>Entregador *</Label>
              <Select value={assignForm.deliverer_id} onValueChange={handleAssignDelivererChange}>
                <SelectTrigger data-testid="assign-deliverer-select">
                  <SelectValue placeholder="Selecciona un entregador" />
                </SelectTrigger>
                <SelectContent>
                  {deliverers.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between border rounded p-3">
              <div className="flex-1 pr-3">
                <Label className="font-medium">El entregador se queda con el costo del envío</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Activa si lo entrega en su propio auto. El costo del envío NO se cobrará al entregador.
                </p>
              </div>
              <Switch
                checked={assignForm.deliverer_keeps_shipping}
                onCheckedChange={(checked) => setAssignForm({ ...assignForm, deliverer_keeps_shipping: checked })}
                data-testid="assign-keeps-shipping-switch"
              />
            </div>
            <div className="bg-primary/5 p-3 rounded text-sm">
              <div className="flex justify-between font-bold">
                <span>Deuda al entregador:</span>
                <span>
                  ${(
                    (assignDialog.shipment?.items_total || 0) +
                    (assignForm.deliverer_keeps_shipping ? 0 : (assignDialog.shipment?.shipping_cost || 0))
                  ).toFixed(2)}
                </span>
              </div>
            </div>
            {assignDialog.shipment?.deliverer_id &&
             assignDialog.shipment?.deliverer_id !== assignForm.deliverer_id &&
             assignForm.deliverer_id && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-2 text-xs">
                🔄 Estás <b>reasignando</b> el envío. Se solicitará aprobación al aprobador por Telegram.
              </div>
            )}
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setAssignDialog({ open: false, shipment: null })}>
                Cancelar
              </Button>
              <Button onClick={submitAssign} data-testid="confirm-assign-button">
                Asignar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Seller Dialog */}
      <Dialog open={changeSellerDlg.open} onOpenChange={(o) => !o && setChangeSellerDlg({ open: false, shipment: null, seller_id: '', reason: '', saving: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cambiar vendedor — {changeSellerDlg.shipment?.folio}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 p-3 rounded text-sm space-y-1">
              <div className="flex justify-between">
                <span>Cliente:</span>
                <span className="font-medium">{changeSellerDlg.shipment?.client_name || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span>Vendedor actual:</span>
                <span className="font-medium">{sellers.find(s => s.id === changeSellerDlg.shipment?.seller_id)?.name || 'Sin vendedor'}</span>
              </div>
              <div className="flex justify-between">
                <span>Comisión total:</span>
                <span className="font-medium">${(changeSellerDlg.shipment?.total_commission || 0).toFixed(2)}</span>
              </div>
            </div>
            <div>
              <Label>Nuevo vendedor *</Label>
              <Select value={changeSellerDlg.seller_id} onValueChange={(v) => setChangeSellerDlg(prev => ({ ...prev, seller_id: v }))}>
                <SelectTrigger data-testid="change-seller-select">
                  <SelectValue placeholder="Selecciona un vendedor" />
                </SelectTrigger>
                <SelectContent>
                  {sellers.filter(s => s.active !== false).map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Motivo del cambio {!isAdmin && <span className="text-rose-600">*</span>}</Label>
              <Input
                value={changeSellerDlg.reason}
                onChange={(e) => setChangeSellerDlg(prev => ({ ...prev, reason: e.target.value }))}
                placeholder={isAdmin ? 'Opcional' : 'Ej. venta atribuida por error al vendedor equivocado'}
                data-testid="change-seller-reason-input"
              />
            </div>
            {isAdmin ? (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded p-2 text-xs">
                ✅ Como admin, este cambio se aplica <b>directamente</b>. La comisión se acreditará al nuevo vendedor cuando se cobre el envío.
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-2 text-xs">
                🔔 Este cambio se enviará al aprobador por Telegram. La comisión se acreditará al nuevo vendedor cuando se cobre el envío.
              </div>
            )}
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setChangeSellerDlg({ open: false, shipment: null, seller_id: '', reason: '', saving: false })}>
                Cancelar
              </Button>
              <Button
                onClick={submitChangeSeller}
                disabled={changeSellerDlg.saving}
                data-testid="confirm-change-seller-button"
              >
                {changeSellerDlg.saving ? 'Procesando…' : (isAdmin ? 'Aplicar cambio' : 'Enviar a aprobación')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(o) => !o && setDetailDialog({ open: false, shipment: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalle del Envío {detailDialog.shipment?.folio}</DialogTitle>
          </DialogHeader>
          {detailDialog.shipment && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">Cliente</p>
                  <p className="font-medium">{detailDialog.shipment.client_name}</p>
                  <p className="text-xs">{detailDialog.shipment.client_phone}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Estado</p>
                  <p>{statusBadge(detailDialog.shipment.status)}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-muted-foreground text-xs">Dirección</p>
                  <p>{detailDialog.shipment.shipping_address}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Vendedor</p>
                  <p>{sellerName(detailDialog.shipment.seller_id)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Entregador</p>
                  <p>{detailDialog.shipment.deliverer_id ? delivererName(detailDialog.shipment.deliverer_id) : '—'}</p>
                </div>
                {detailDialog.shipment.notes && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground text-xs">Notas</p>
                    <p>{detailDialog.shipment.notes}</p>
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm font-semibold mb-2">Productos</p>
                <table className="w-full text-sm border">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-2 py-1 text-left">Producto</th>
                      <th className="px-2 py-1 text-right">Cant.</th>
                      <th className="px-2 py-1 text-right">P. Unit.</th>
                      <th className="px-2 py-1 text-right">Subtotal</th>
                      <th className="px-2 py-1 text-right">Comisión</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailDialog.shipment.items?.map((it, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1">{it.product_name}</td>
                        <td className="px-2 py-1 text-right">{it.quantity}</td>
                        <td className="px-2 py-1 text-right">${it.unit_price.toFixed(2)}</td>
                        <td className="px-2 py-1 text-right">${(it.unit_price * it.quantity).toFixed(2)}</td>
                        <td className="px-2 py-1 text-right text-emerald-600">${((it.commission || 0) * it.quantity).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-slate-50 p-3 rounded text-sm space-y-1">
                <div className="flex justify-between">
                  <span>Total productos:</span>
                  <span>${(detailDialog.shipment.items_total || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Costo envío:</span>
                  <span>${(detailDialog.shipment.shipping_cost || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Comisión vendedor:</span>
                  <span className="text-emerald-600">${(detailDialog.shipment.total_commission || 0).toFixed(2)}</span>
                </div>
                {detailDialog.shipment.status !== 'paid' && (
                  <div className="flex justify-between font-bold border-t pt-1">
                    <span>Deuda al entregador:</span>
                    <span>${calcDebt(detailDialog.shipment).toFixed(2)}</span>
                  </div>
                )}
                {detailDialog.shipment.deliverer_keeps_shipping && (
                  <p className="text-xs text-emerald-600">El entregador se queda con el costo del envío.</p>
                )}
              </div>

              <div className="text-xs text-muted-foreground">
                Creado: {new Date(detailDialog.shipment.created_at).toLocaleString()}
                {detailDialog.shipment.assigned_at && ` · Asignado: ${new Date(detailDialog.shipment.assigned_at).toLocaleString()}`}
                {detailDialog.shipment.paid_at && ` · Pagado: ${new Date(detailDialog.shipment.paid_at).toLocaleString()}`}
              </div>

              {detailDialog.shipment.status !== 'paid' && (
                <div className="flex justify-end pt-2 border-t">
                  <Button
                    variant="outline"
                    onClick={() => openEditItems(detailDialog.shipment)}
                    data-testid="edit-items-from-detail"
                    className="border-amber-400 text-amber-700 hover:bg-amber-50"
                    title={isAdmin ? 'Editar productos del envío' : 'Solicita aprobación al admin'}
                  >
                    <Pencil size={16} className="mr-2" />
                    Editar items{!isAdmin && ' (aprobación)'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Editar items del envío */}
      <Dialog
        open={editItemsDlg.open}
        onOpenChange={(o) => !o && !editItemsDlg.saving && setEditItemsDlg({ open: false, shipment: null, items: [], reason: '', search: '', saving: false })}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-items-dialog">
          <DialogHeader>
            <DialogTitle>📦 Editar items del envío {editItemsDlg.shipment?.folio}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-xs text-muted-foreground">
              Modifica cantidades, elimina o agrega productos. Se recalcula el total y, si está asignado, la deuda del entregador.
              {!isAdmin && ' Como no eres admin, esta acción se enviará a aprobación por Telegram.'}
            </p>

            {/* Buscador para agregar productos */}
            <div>
              <Label className="text-xs">Agregar producto</Label>
              <Input
                value={editItemsDlg.search}
                onChange={(e) => setEditItemsDlg(s => ({ ...s, search: e.target.value }))}
                placeholder="Buscar por nombre o SKU…"
                data-testid="edit-items-search"
              />
              {editItemsDlg.search.trim() && (
                <div className="border rounded mt-1 max-h-40 overflow-y-auto bg-white">
                  {(products || [])
                    .filter(p => {
                      const q = editItemsDlg.search.trim().toLowerCase();
                      return (
                        (p.name || '').toLowerCase().includes(q) ||
                        (p.sku || '').toLowerCase().includes(q)
                      );
                    })
                    .slice(0, 20)
                    .map(p => (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => addProductToEdit(p)}
                        className="w-full text-left px-2 py-1.5 hover:bg-amber-50 border-b text-xs flex justify-between items-center"
                        data-testid={`edit-items-add-${p.id}`}
                      >
                        <span>
                          <b>{p.name}</b>
                          {p.sku && <span className="text-muted-foreground ml-1">({p.sku})</span>}
                        </span>
                        <span className="text-muted-foreground">${Number(p.precio_1 || 0).toFixed(2)} · stock {p.stock ?? 0}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>

            {/* Items actuales */}
            <div>
              <div className="font-semibold mb-1">Items</div>
              <table className="w-full text-xs border">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-2 py-1 text-left">Producto</th>
                    <th className="px-2 py-1 text-right">Cant.</th>
                    <th className="px-2 py-1 text-right">P. Unit.</th>
                    <th className="px-2 py-1 text-right">Subtotal</th>
                    <th className="px-2 py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {editItemsDlg.items.map((it, idx) => (
                    <tr key={idx} className="border-t" data-testid={`edit-item-row-${idx}`}>
                      <td className="px-2 py-1">{it.product_name}</td>
                      <td className="px-2 py-1 text-right">
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          value={it.quantity}
                          onChange={(e) => {
                            // Sólo enteros — descarta decimales y signos
                            const v = e.target.value.replace(/[^\d]/g, '');
                            updateEditItem(idx, 'quantity', v === '' ? '' : parseInt(v, 10));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === '.' || e.key === ',' || e.key === '-' || e.key === 'e') e.preventDefault();
                          }}
                          className="h-7 w-20 text-right inline-block"
                          data-testid={`edit-item-qty-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-1 text-right">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={it.unit_price}
                          onChange={(e) => updateEditItem(idx, 'unit_price', e.target.value)}
                          className="h-7 w-24 text-right inline-block"
                          data-testid={`edit-item-price-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-1 text-right font-semibold">
                        ${(Number(it.unit_price) * Number(it.quantity) || 0).toFixed(2)}
                      </td>
                      <td className="px-2 py-1 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeEditItem(idx)}
                          data-testid={`edit-item-remove-${idx}`}
                        >
                          <Trash size={14} className="text-rose-600" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {editItemsDlg.items.length === 0 && (
                    <tr><td colSpan="5" className="px-2 py-3 text-center text-muted-foreground">Sin items. Agrega al menos uno.</td></tr>
                  )}
                </tbody>
                <tfoot className="bg-slate-50 font-bold">
                  <tr>
                    <td colSpan="3" className="px-2 py-1 text-right">Total:</td>
                    <td className="px-2 py-1 text-right">
                      ${editItemsDlg.items.reduce((s, it) => s + (Number(it.unit_price) * Number(it.quantity) || 0), 0).toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div>
              <Label className="text-xs">
                Motivo {!isAdmin && <span className="text-rose-600">*</span>}
              </Label>
              <Input
                value={editItemsDlg.reason}
                onChange={(e) => setEditItemsDlg(s => ({ ...s, reason: e.target.value }))}
                placeholder="Ej: cliente cambió 1 producto"
                data-testid="edit-items-reason"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => setEditItemsDlg({ open: false, shipment: null, items: [], reason: '', search: '', saving: false })}
                disabled={editItemsDlg.saving}
              >Cancelar</Button>
              <Button
                onClick={submitEditItems}
                disabled={editItemsDlg.saving || editItemsDlg.items.length === 0}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="edit-items-submit"
              >
                {editItemsDlg.saving ? 'Procesando…' : (isAdmin ? 'Aplicar cambios' : 'Enviar a aprobación')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New/Edit Deliverer Dialog */}
      <Dialog open={showDelivererDialog} onOpenChange={setShowDelivererDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingDeliverer ? 'Editar Entregador' : 'Nuevo Entregador'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitDeliverer} className="space-y-4" data-testid="deliverer-form">
            <div>
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={delivererForm.name}
                onChange={(e) => setDelivererForm({ ...delivererForm, name: e.target.value })}
                data-testid="deliverer-name-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="phone">Teléfono</Label>
              <Input
                id="phone"
                value={delivererForm.phone}
                onChange={(e) => setDelivererForm({ ...delivererForm, phone: e.target.value })}
                data-testid="deliverer-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="nip">NIP (4 dígitos) — para portal <code>/entregador</code></Label>
              <Input
                id="nip"
                type="text"
                inputMode="numeric"
                maxLength={4}
                pattern="[0-9]{4}"
                placeholder={editingDeliverer?.nip || 'Se autogenera si lo dejas vacío'}
                value={delivererForm.nip}
                onChange={(e) => setDelivererForm({ ...delivererForm, nip: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                data-testid="deliverer-nip-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Debe ser único. Si lo dejas vacío al crear, se genera automático.</p>
            </div>
            <div className="flex items-center justify-between border rounded p-3">
              <div className="flex-1 pr-3">
                <Label className="font-medium">Siempre se queda con el costo del envío</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Si el entregador siempre entrega en su propio auto. Esto se usará por defecto al asignar envíos (puedes cambiarlo por envío).
                </p>
              </div>
              <Switch
                checked={delivererForm.default_keeps_shipping}
                onCheckedChange={(checked) => setDelivererForm({ ...delivererForm, default_keeps_shipping: checked })}
                data-testid="deliverer-default-keeps-switch"
              />
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDelivererDialog(false)} data-testid="cancel-deliverer-button">
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-deliverer-button">
                {editingDeliverer ? 'Actualizar' : 'Crear Entregador'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Telegram dialog (deliverer) */}
      <Dialog open={tgDialog.open} onOpenChange={(o) => !o && setTgDialog({ open: false, deliverer: null, code: null, loading: false })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TelegramLogo size={22} weight="fill" className="text-blue-500" />
              Telegram — {currentDelivererInDialog?.name}
            </DialogTitle>
          </DialogHeader>

          {!botUsername && (
            <div className="bg-amber-50 border border-amber-300 text-amber-900 text-sm rounded p-3">
              ⚠️ El bot de Telegram no está configurado.
            </div>
          )}

          {currentDelivererInDialog?.telegram_chat_id ? (
            <div className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-300 rounded p-3 text-sm">
                <p className="font-medium text-emerald-900">✅ Vinculado correctamente</p>
                {currentDelivererInDialog.telegram_username && (
                  <p className="text-emerald-700 mt-1">Usuario: @{currentDelivererInDialog.telegram_username}</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={sendTgTestMessage} className="flex-1" data-testid="tg-test-deliverer-button">
                  <PaperPlaneTilt size={16} className="mr-1" />
                  Mensaje de prueba
                </Button>
                <Button onClick={unlinkTg} variant="destructive" data-testid="tg-unlink-deliverer-button">
                  <LinkBreak size={16} className="mr-1" />
                  Desvincular
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Genera un código y compártelo con el entregador. Recibirá notificaciones cuando se le asigne, cancele o cobre un envío.
              </p>
              {!tgDialog.code ? (
                <Button onClick={generateTgCode} disabled={tgDialog.loading || !botUsername} className="w-full" data-testid="tg-generate-deliverer-code">
                  {tgDialog.loading ? 'Generando...' : 'Generar código de vinculación'}
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="bg-slate-50 border rounded p-3">
                    <Label className="text-xs text-muted-foreground">Código</Label>
                    <div className="flex items-center gap-2">
                      <code className="text-2xl font-mono font-bold tracking-widest flex-1">{tgDialog.code}</code>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(tgDialog.code)}>
                        <Copy size={16} />
                      </Button>
                    </div>
                  </div>
                  {tgStartLink && (
                    <div className="bg-blue-50 border border-blue-300 rounded p-3 space-y-2">
                      <p className="text-sm font-medium text-blue-900">Enlace mágico:</p>
                      <a href={tgStartLink} target="_blank" rel="noopener noreferrer"
                         className="block bg-blue-500 hover:bg-blue-600 text-white text-center font-medium py-2 rounded">
                        Abrir en Telegram
                      </a>
                      <div className="flex items-center gap-2">
                        <input readOnly value={tgStartLink} className="flex-1 text-xs bg-white border rounded px-2 py-1 font-mono" />
                        <Button size="sm" variant="ghost" onClick={() => copyToClipboard(tgStartLink)}>
                          <Copy size={14} />
                        </Button>
                      </div>
                      <p className="text-xs text-blue-700">
                        Si la persona también es vendedora, puede usar el mismo Telegram para ambos roles (genera un código en cada lado).
                      </p>
                    </div>
                  )}
                  <Button onClick={refreshDeliverers} variant="outline" className="w-full">
                    Verificar estado de vinculación
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};
